import { authApiClient } from "@api-client/auth";
import { authGlobalApiClient } from "@api-client/auth_global";
import { setAuthState } from "@api-client/auth/auth";
import { setPasskeyUsedLocally } from "@auth-frontend-vue/lib/passkeyLocalStorage";
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type { AuthenticationResponseJSON, RegistrationResponseJSON } from '@simplewebauthn/browser';

async function verifyPasskeyLogin(authResponse: AuthenticationResponseJSON) {
    const { data, error } = await (await authApiClient.passkey['passkey-login-verify'].$post({
        json: {
            response: authResponse,
        },
    })).json();
    if (!error) {
        setPasskeyUsedLocally();
        await setAuthState(data);
    }
    return { data, error };
}

export const checkEmail = async (values: { email: string }) => {
    const { data, error } = await (await authApiClient.email_password['check-email'].$post({
        form: values,
    })).json()
    return { data, error };
}

export const checkPhone = async (values: { phoneNumber: string }) => {
    const { data, error } = await (await authApiClient.phone['check-phone'].$post({
        form: values,
    })).json()
    return { data, error };
}

export const sendOTP = async (values: { verifyEmail: string, turnstile: string }) => {
    const { data, error } = await (await authApiClient.otp.send.$post({
        form: values,
    })).json()
    return { data, error };
}

export const sendPhoneOTP = async (values: { phoneNumber: string, turnstile: string }) => {
    const { data, error } = await (await authApiClient.phone.otp.send.$post({
        form: values,
    })).json()
    return { data, error };
}

export const registerEmail = async (values: {
    email: string,
    name: string,
    password: string,
    turnstile: string
}) => {
    const { data, error } = await (await authApiClient.email_password.register.$post({
        form: values,
    })).json()
    return { data, error };
}

export const registerPhone = async (values: {
    phoneNumber: string,
    name: string,
    password: string,
    turnstile: string
}) => {
    const { data, error } = await (await authApiClient.phone.register.$post({
        form: values,
    })).json()
    return { data, error };
}

export const verifyOTP = async (values: { verifyEmail: string, code: string }) => {
    const { data, error } = await (await authApiClient.otp.verify.$post({
        form: {
            verifyEmail: values.verifyEmail,
            code: values.code,
        },
    })).json()
    if (!error) {
        await setAuthState(data);
    }
    return { data, error };
}

export const verifyPhoneOTP = async (values: { phoneNumber: string, code: string }) => {
    const { data, error } = await (await authApiClient.phone.otp.verify.$post({
        form: {
            phoneNumber: values.phoneNumber,
            code: values.code,
        },
    })).json()
    if (!error) {
        await setAuthState(data);
    }
    return { data, error };
}

export const verifyPhoneSignup = async (values: { phoneNumber: string, code: string }) => {
    const { data, error } = await (await authApiClient.phone.verify.$post({
        form: {
            phoneNumber: values.phoneNumber,
            code: values.code,
        },
    })).json()
    if (!error) {
        await setAuthState(data);
    }
    return { data, error };
}

export const verifySignup = async (values: { verifyEmail: string, code: string }) => {
    const { data, error } = await (await authApiClient.email_password.verify.$post({
        form: {
            verifyEmail: values.verifyEmail,
            code: values.code,
        },
    })).json()
    if (!error) {
        await setAuthState(data);
    }
    return { data, error };
}

export const login = async (values: { email: string, password: string }) => {
    const { data, error } = await (await authApiClient.email_password.login.$post({
        form: {
            email: values.email,
            password: values.password,
        },
    })).json()
    if (!error) {
        await setAuthState(data);
    }
    return { data, error };
}

export const loginPhone = async (values: { phoneNumber: string, password: string }) => {
    const { data, error } = await (await authApiClient.phone.login.$post({
        form: {
            phoneNumber: values.phoneNumber,
            password: values.password,
        },
    })).json()
    if (!error) {
        await setAuthState(data);
    }
    return { data, error };
}

export const authorize = async (values: { code_challenge: string, redirect_uri: string, state: string }) => {
    const { data, error } = await (await authApiClient.pkce.authorize.$post({
        form: values,
    })).json();
    // if (error) {
    //     if (error.code === "INVALID_REFRESH_TOKEN") {
    //         await setAuthState(emptyState);
    //     }
    // }
    return { data, error };
}

export const passkeyLogin = async () => {
    const { data: options, error: optionsError } = await (await authApiClient
        .passkey['passkey-login-options'].$post()).json();
    if (optionsError) {
        return { data: null, error: optionsError };
    }

    let authResponse: AuthenticationResponseJSON;
    try {
        authResponse = await startAuthentication({ optionsJSON: options });
    } catch {
        return { data: null, error: { message: 'Passkey sign-in was cancelled', code: 'PASSKEY_CANCELLED' } };
    }

    return verifyPasskeyLogin(authResponse);
}

export const passkeyLoginConditional = async () => {
    const { data: options, error: optionsError } = await (await authApiClient
        .passkey['passkey-login-options'].$post()).json();
    if (optionsError) {
        return { data: null, error: optionsError };
    }

    let authResponse: AuthenticationResponseJSON;
    try {
        authResponse = await startAuthentication({
            optionsJSON: options,
            useBrowserAutofill: true,
        });
    } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
            return { data: null, error: { message: 'Passkey sign-in was cancelled', code: 'PASSKEY_CANCELLED' } };
        }
        return { data: null, error: { message: 'Passkey sign-in was cancelled', code: 'PASSKEY_CANCELLED' } };
    }

    return verifyPasskeyLogin(authResponse);
}

export const passkeyRegister = async () => {
    const { data: options, error: optionsError } = await (await authGlobalApiClient.passkey['passkey-register-options'].$post()).json();
    if (optionsError) {
        return { data: null, error: optionsError };
    }

    let registrationResponse: RegistrationResponseJSON;
    try {
        registrationResponse = await startRegistration({ optionsJSON: options });
    } catch {
        return { data: null, error: { message: 'Passkey registration was cancelled', code: 'PASSKEY_CANCELLED' } };
    }

    const { data, error } = await (await authGlobalApiClient.passkey['passkey-register-verify'].$post({
        json: {
            response: registrationResponse,
        },
    })).json();
    if (!error) {
        setPasskeyUsedLocally();
    }
    return { data, error };
}